// ===============================
// UC Menu Items V2 API Communication
// ===============================

import { useCallback, useMemo, useRef, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { createExperienceTracker } from '@atlassian/navigation-perf-utils';
import {
	type AppMenuConfig,
	encodeConfig,
	type PersonalizeMode,
} from '@atlassian/universal-create-common/bin-param';

import { RequestStatus, UC_MENU_ITEMS_REQUEST_STATUS_KEY } from '../../constants';
import { useBFFContext } from '../context';
import { fetchJSON } from '../utils/request';

import { useGenericCache } from './useGenericCache';
import { useStatus } from './useStatus';
import type { SuggestionItemResponse } from './useUCSuggestions';
import { useURLBuilder } from './useURLBuilder';

// ===============================
// Types
// ===============================

/**
 * Native menu item returned as-is from the request
 * @private
 */
export interface NativeMenuItemResponse {
	ati: string;
	promoted?: boolean;
	type: 'native';
}

/**
 * Response item for cross-app items
 * @private
 */
export type CrossAppItemResponse = SuggestionItemResponse;

/**
 * Union type for all processed menu items
 * @private
 */
export type ProcessedMenuItem = NativeMenuItemResponse | CrossAppItemResponse;

/**
 * Processed suggestion section in the response
 * @private
 */
export interface ProcessedSuggestionSection {
	items: CrossAppItemResponse[];
	limit: number;
	type: 'suggestionSection';
}

/**
 * Processed app-defined section in the response
 * @private
 */
export interface ProcessedAppDefinedSection {
	items: ProcessedMenuItem[];
	personalized: PersonalizeMode;
	type: 'appDefinedSection';
}

/**
 * Union type for processed sections
 * @private
 */
export type ProcessedMenuSection = ProcessedSuggestionSection | ProcessedAppDefinedSection;

/**
 * Complete V2 menu items API response
 * @private
 */
export interface MenuItemsAPIResponse {
	isPartial?: boolean;
	sections: ProcessedMenuSection[];
}

/**
 * Props for useUCMenuItems hook
 * @private
 */
interface UseUCMenuItemsProps {
	appMenuConfig: AppMenuConfig;
	cloudId: string;
	locale?: string;
	orgId: string;
	product: string;
	skipInitialization?: boolean;
	subProduct?: string;
	userId?: string;
}

/**
 * Return type for useUCMenuItems hook
 * @private
 */
export interface UseUCMenuItemsReturn {
	data: MenuItemsAPIResponse | undefined;
	fetchUCMenuItems: () => Promise<MenuItemsAPIResponse | undefined>;
	hasError: boolean;
	isLoading: boolean;
	status: RequestStatus | null;
}

// ===============================
// Hook Implementation
// ===============================

/**
 * Hook for fetching UC menu items V2 with smart caching and binary encoding
 *
 * @private Use in Universal Create only
 * This hook:
 * - Encodes the appMenuConfig using binary encoding (d, ro, rt, ap params)
 * - Fetches menu items from the V2 endpoint
 * - Handles caching, loading states, and errors
 * - Follows the same patterns as useUCSuggestions
 */
export function useUCMenuItems({
	cloudId,
	orgId,
	product,
	userId,
	locale,
	subProduct,
	appMenuConfig,
	skipInitialization = false,
}: UseUCMenuItemsProps): UseUCMenuItemsReturn {
	const { value, currentLoadingRequests } = useBFFContext();
	const { setStatus } = useStatus();
	const [data, setData] = useState<MenuItemsAPIResponse | undefined>(undefined);
	const [hasError, setHasError] = useState(false);

	const { buildUCMenuItemsURL } = useURLBuilder({ cloudId });

	// Encode the config to get binary parameters
	const encodedConfig = useMemo(() => {
		if (skipInitialization) {
			return null;
		}

		try {
			return encodeConfig(appMenuConfig);
		} catch {
			// If encoding fails, we'll handle it in the fetch
			return null;
		}
	}, [appMenuConfig, skipInitialization]);

	// Generate cache key based on encoded config and metadata
	const { cachedObject, setCachedObject } = useGenericCache<MenuItemsAPIResponse>({
		cloudId,
		productType: product,
		orgId,
		accountId: userId,
		subProduct,
		locale,
		// Include encoded config in cache key to ensure different configs get different cache entries
		...(encodedConfig && { encodedConfigD: encodedConfig.d }),
	});

	const requestIdRef = useRef(0);
	const inFlightRequestKeyRef = useRef<string | null>(null);

	// Set data from cache on mount if available
	if (cachedObject && !data) {
		setData(cachedObject);
		setHasError(false);
	}

	const fetchUCMenuItems = useCallback(async (): Promise<MenuItemsAPIResponse | undefined> => {
		// Kill switch: return early if feature flag is disabled
		if (!fg('navx_uc_cross_app_creation_v2_enabled')) {
			return undefined;
		}

		// Early exit: filter out invalid param requests when required identifiers are missing or skip initialization if requested
		if (!cloudId || !orgId || skipInitialization) {
			return undefined;
		}

		// Early exit: if encoding failed
		if (!encodedConfig) {
			setHasError(true);
			setStatus(RequestStatus.ERROR, UC_MENU_ITEMS_REQUEST_STATUS_KEY);
			return undefined;
		}

		// Return cached data if available (already locale-scoped via cache key)
		if (cachedObject) {
			setStatus(RequestStatus.COMPLETE, UC_MENU_ITEMS_REQUEST_STATUS_KEY);
			return cachedObject;
		}

		// Generate request key to prevent duplicate concurrent requests
		const requestKey = `${product}|${orgId}|${userId}|${locale}|${subProduct}|${encodedConfig.d}`;

		// Skip if an identical request is already in progress
		if (inFlightRequestKeyRef.current === requestKey) {
			return undefined;
		}

		// Mark this request as in-flight
		inFlightRequestKeyRef.current = requestKey;

		// Increment request id for race protection
		requestIdRef.current += 1;
		const scopedId = requestIdRef.current;

		// Create and start experience tracker only when actually making request
		const experience = fg('navx-2747-ufo-prefetch-tracking')
			? createExperienceTracker('universalCreatePrefetch')
			: null;
		experience?.start();

		try {
			const url = buildUCMenuItemsURL({
				product,
				orgId,
				userId,
				locale,
				subProduct,
				d: encodedConfig.d,
				ro: encodedConfig.ro,
				rt: encodedConfig.rt,
				ap: encodedConfig.ap,
			});
			setStatus(RequestStatus.LOADING, UC_MENU_ITEMS_REQUEST_STATUS_KEY);

			const response = await fetchJSON<MenuItemsAPIResponse>(url, {});

			// Ignore if this response is for a stale request
			if (scopedId !== requestIdRef.current) {
				// Clear in-flight key if this was the request that set it
				if (inFlightRequestKeyRef.current === requestKey) {
					inFlightRequestKeyRef.current = null;
				}
				return undefined;
			}

			if (response.status === RequestStatus.ERROR) {
				experience?.failure();
				setHasError(true);
				setData(undefined);
				setStatus(RequestStatus.ERROR, UC_MENU_ITEMS_REQUEST_STATUS_KEY);
				// Clear in-flight key on error
				inFlightRequestKeyRef.current = null;
				return undefined;
			}

			if (response.status === RequestStatus.COMPLETE && response.data) {
				experience?.success();
				setData(response.data);
				setHasError(false);

				// Only cache if the data is not partial
				if (!response.data.isPartial) {
					setCachedObject(response.data);
				}

				setStatus(RequestStatus.COMPLETE, UC_MENU_ITEMS_REQUEST_STATUS_KEY);
				// Clear in-flight key on success
				inFlightRequestKeyRef.current = null;
				return response.data;
			}
		} catch {
			experience?.failure();

			if (scopedId === requestIdRef.current) {
				setHasError(true);
				setData(undefined);
				setStatus(RequestStatus.ERROR, UC_MENU_ITEMS_REQUEST_STATUS_KEY);
			}
			// Clear in-flight key on exception
			inFlightRequestKeyRef.current = null;
		}

		return undefined;
	}, [
		cloudId,
		cachedObject,
		orgId,
		product,
		userId,
		locale,
		subProduct,
		encodedConfig,
		buildUCMenuItemsURL,
		setStatus,
		setCachedObject,
		skipInitialization,
	]);

	const isLoading = useMemo(() => {
		if (fg('navx_uc_cross_app_creation_v2_enabled')) {
			if (skipInitialization) {
				return false;
			}

			// We check if the current loading requests include UC menu items rather than the BFF status directly
			// to ensure that it only returns true for UC menu items and not other BFF requests.
			const isBffLoadingOurStuff =
				currentLoadingRequests?.includes(UC_MENU_ITEMS_REQUEST_STATUS_KEY) ?? false;

			// That said, if the BFF is in loading state but data has been returned, we want to wait for things to complete
			// before running things like onResultChange, so that we don't do those things an unnecessary extra time
			// during the brief moment when data is present but loading is still true, or an error has occurred and loading is still true.
			const hasMenuItemDataBeenReturned = data !== undefined || hasError;

			return isBffLoadingOurStuff && !hasMenuItemDataBeenReturned;
		}

		return false;
	}, [currentLoadingRequests, data, hasError, skipInitialization]);

	return {
		data,
		isLoading,
		hasError,
		status: value.status,
		fetchUCMenuItems,
	};
}
